import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  ArrowLeft, 
  Mail,
  Home,
  Target,
  Plus,
  Trash2,
  Send,
  Copy,
  CheckCircle,
  Clock,
  Link,
  User,
  Users
} from "lucide-react";

export default function InvitesPage({ evaluator }) {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newInvite, setNewInvite] = useState({ email: "", name: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdInviteUrl, setCreatedInviteUrl] = useState("");

  useEffect(() => {
    fetchInvites();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInvites = async () => {
    try {
      const res = await axios.get(`${API}/player-invites`);
      setInvites(res.data);
    } catch (error) {
      toast.error("Erro ao carregar convites");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    if (!newInvite.email) {
      toast.error("Email é obrigatório");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/player-invites`, newInvite);
      toast.success("Convite enviado com sucesso!");
      setCreatedInviteUrl(res.data.invite_url);
      setNewInvite({ email: "", name: "" });
      fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar convite");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendInvite = async (token) => {
    try {
      await axios.post(`${API}/player-invites/${token}/resend`);
      toast.success("Email reenviado!");
    } catch (error) {
      toast.error("Erro ao reenviar email");
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    if (!window.confirm("Deseja excluir este convite?")) return;
    try {
      await axios.delete(`${API}/player-invites/${inviteId}`);
      toast.success("Convite excluído");
      fetchInvites();
    } catch (error) {
      toast.error("Erro ao excluir convite");
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const getInviteUrl = (token) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/register/${token}`;
  };

  const canAccess = evaluator?.role === 'admin' || evaluator?.role === 'tecnico';

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Acesso Restrito</h2>
            <p className="text-muted-foreground">Apenas administradores e técnicos podem enviar convites.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">
              Voltar ao Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">Convites de Jogadores</h1>
                <p className="text-sm text-muted-foreground">Envie links para jogadores se cadastrarem</p>
              </div>
            </div>
            <Button onClick={() => { setDialogOpen(true); setCreatedInviteUrl(""); }}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Convite
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-card border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total de Convites</p>
                  <p className="text-2xl font-bold">{invites.length}</p>
                </div>
                <Mail className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pendentes</p>
                  <p className="text-2xl font-bold text-yellow-400">
                    {invites.filter(i => i.status === 'pending').length}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Cadastrados</p>
                  <p className="text-2xl font-bold text-green-400">
                    {invites.filter(i => i.status === 'registered').length}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invites Table */}
        <Card className="bg-card border-border/50 shadow-xl">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Lista de Convites
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Carregando...</div>
            ) : invites.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum convite enviado ainda.</p>
                <Button onClick={() => setDialogOpen(true)} className="mt-4">
                  <Plus className="mr-2 h-4 w-4" />
                  Enviar Primeiro Convite
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead>Email</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map(invite => (
                    <TableRow key={invite.id} className="border-border/50">
                      <TableCell className="font-medium">{invite.email}</TableCell>
                      <TableCell>{invite.name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant={invite.status === 'registered' ? 'default' : 'secondary'}
                               className={invite.status === 'registered' ? 'bg-green-500' : 'bg-yellow-500'}>
                          {invite.status === 'registered' ? (
                            <><CheckCircle className="h-3 w-3 mr-1" />Cadastrado</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" />Pendente</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(invite.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => copyToClipboard(getInviteUrl(invite.token))}
                            title="Copiar link"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          {invite.status === 'pending' && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleResendInvite(invite.token)}
                              title="Reenviar email"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDeleteInvite(invite.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Invite Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Enviar Convite para Jogador</DialogTitle>
          </DialogHeader>
          
          {createdInviteUrl ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <div className="flex items-center gap-2 text-green-500 mb-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-bold">Convite Enviado!</span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  O email foi enviado. Você também pode copiar o link abaixo:
                </p>
                <div className="flex items-center gap-2">
                  <Input value={createdInviteUrl} readOnly className="bg-input/50 text-xs" />
                  <Button size="icon" onClick={() => copyToClipboard(createdInviteUrl)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Button onClick={() => { setDialogOpen(false); setCreatedInviteUrl(""); }} className="w-full">
                Fechar
              </Button>
            </div>
          ) : (
            <form onSubmit={handleCreateInvite} className="space-y-4">
              <div className="space-y-2">
                <Label>Email do Jogador *</Label>
                <Input
                  type="email"
                  value={newInvite.email}
                  onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                  placeholder="jogador@email.com"
                  className="bg-input/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Nome do Jogador (opcional)</Label>
                <Input
                  value={newInvite.name}
                  onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                  placeholder="Nome do jogador"
                  className="bg-input/50"
                />
                <p className="text-xs text-muted-foreground">
                  Se informado, o nome aparecerá no email de convite.
                </p>
              </div>
              <Button type="submit" className="w-full bg-primary" disabled={isSubmitting}>
                <Mail className="mr-2 h-4 w-4" />
                {isSubmitting ? "Enviando..." : "Enviar Convite"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Home className="h-5 w-5" />
          <span className="text-xs">Início</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/invites')} className="flex flex-col items-center gap-1 h-auto py-2 text-primary">
          <Mail className="h-5 w-5" />
          <span className="text-xs">Convites</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/players')} className="flex flex-col items-center gap-1 h-auto py-2">
          <User className="h-5 w-5" />
          <span className="text-xs">Jogadores</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/evaluate')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Target className="h-5 w-5" />
          <span className="text-xs">Avaliar</span>
        </Button>
      </nav>
    </div>
  );
}
